import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Table,
  Space,
  Button,
  Divider,
  Spin,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useInspections } from "../../../hooks/academics/useInspections";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const InspectionDetailDrawer = ({
  visible,
  onClose,
  inspectionId,
  onDownloadPdf,
  onRegeneratePdf,
}) => {
  const { inspectionDetails, loading, fetchInspectionDetails } =
    useInspections();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [regeneratingPdf, setRegeneratingPdf] = useState(false);

  useEffect(() => {
    if (visible && inspectionId) {
      fetchInspectionDetails(inspectionId);
    }
    if (!visible) {
      setDownloadingPdf(false);
      setRegeneratingPdf(false);
    }
  }, [visible, inspectionId, fetchInspectionDetails]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    await onDownloadPdf(inspectionDetails);
    setDownloadingPdf(false);
  };

  const handleRegeneratePdf = async () => {
    setRegeneratingPdf(true);
    await onRegeneratePdf(inspectionDetails);
    setRegeneratingPdf(false);
  };

  const generalScoresColumns = [
    {
      title: "NO",
      key: "no",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Area",
      dataIndex: "areaLabel",
      key: "areaLabel",
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 80,
      align: "center",
      render: (score) => (
        <Tag color={score >= 7 ? "green" : score >= 5 ? "orange" : "red"}>
          {score}/10
        </Tag>
      ),
    },
    {
      title: "Areas to Maintain",
      dataIndex: "areasToMaintain",
      key: "areasToMaintain",
      ellipsis: true,
    },
    {
      title: "Areas to Improve",
      dataIndex: "areasToImprove",
      key: "areasToImprove",
      ellipsis: true,
    },
  ];

  const skillScoresColumns = [
    {
      title: "NO",
      key: "no",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Area",
      dataIndex: "areaLabel",
      key: "areaLabel",
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 100,
      align: "center",
      render: (score) => (
        <Tag color={score >= 70 ? "green" : score >= 50 ? "orange" : "red"}>
          {score}/100
        </Tag>
      ),
    },
  ];

  const subjectPerformanceColumns = [
    {
      title: "Subject",
      dataIndex: "subjectName",
      key: "subjectName",
    },
    {
      title: "Areas to Maintain",
      dataIndex: "areasToMaintain",
      key: "areasToMaintain",
      ellipsis: true,
    },
    {
      title: "Areas to Improve",
      dataIndex: "areasToImprove",
      key: "areasToImprove",
      ellipsis: true,
    },
    {
      title: "Excelling Students",
      dataIndex: "excellingStudentNames",
      key: "excellingStudentNames",
      render: (names) => names?.join(", ") || "-",
    },
    {
      title: "Students Needing Help",
      dataIndex: "studentsNeedingHelpNames",
      key: "studentsNeedingHelpNames",
      render: (names) => names?.join(", ") || "-",
    },
  ];

  return (
    <Drawer
      title="Inspection Details"
      placement="right"
      width={900}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadPdf}
            loading={downloadingPdf}
            disabled={downloadingPdf || !inspectionDetails}
          >
            Download PDF
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRegeneratePdf}
            loading={regeneratingPdf}
            disabled={regeneratingPdf || !inspectionDetails}
          >
            Regenerate PDF
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : inspectionDetails ? (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Basic Info */}
          <Card size="small">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Class">
                <Text strong>{inspectionDetails.className}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Stream">
                {inspectionDetails.streamName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Term">
                {inspectionDetails.termName}
              </Descriptions.Item>
              <Descriptions.Item label="Academic Year">
                {inspectionDetails.academicYear}
              </Descriptions.Item>
              <Descriptions.Item label="Supervisor">
                {inspectionDetails.supervisorName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {inspectionDetails.createdAt
                  ? dayjs(inspectionDetails.createdAt).format(
                      "DD MMM YYYY HH:mm",
                    )
                  : "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Score Summary */}
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="General Score"
                  value={inspectionDetails.totalGeneralScore}
                  suffix="/100"
                  valueStyle={{
                    color:
                      inspectionDetails.totalGeneralScore >= 70
                        ? "#3f8600"
                        : inspectionDetails.totalGeneralScore >= 50
                          ? "#faad14"
                          : "#cf1322",
                  }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Skill Score"
                  value={inspectionDetails.totalSkillScore}
                  suffix="/300"
                  valueStyle={{
                    color:
                      inspectionDetails.totalSkillScore >= 210
                        ? "#3f8600"
                        : inspectionDetails.totalSkillScore >= 150
                          ? "#faad14"
                          : "#cf1322",
                  }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Subjects Inspected"
                  value={inspectionDetails.subjectPerformances?.length || 0}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
          </Row>

          {/* General Areas */}
          <div>
            <Title level={5}>General Areas</Title>
            <Table
              columns={generalScoresColumns}
              dataSource={inspectionDetails.generalScores}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
            />
          </div>

          {/* Skills */}
          <div>
            <Title level={5}>Skills / Application</Title>
            <Table
              columns={skillScoresColumns}
              dataSource={inspectionDetails.skillScores}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
            />
          </div>

          {/* Subject Performance */}
          {inspectionDetails.subjectPerformances?.length > 0 && (
            <div>
              <Title level={5}>Supervisor's Comments</Title>
              <Table
                columns={subjectPerformanceColumns}
                dataSource={inspectionDetails.subjectPerformances}
                rowKey="id"
                pagination={false}
                size="small"
                bordered
              />
            </div>
          )}
        </Space>
      ) : (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Text type="secondary">No inspection data available</Text>
        </div>
      )}
    </Drawer>
  );
};

export default InspectionDetailDrawer;
